import React, {
  useEffect,
  createContext,
  useState,
  useCallback,
  useMemo
} from 'react';
import PropTypes from 'prop-types';

import { gtagEvent } from 'src/utils/gtag';
import useRestApiClient from 'src/hooks/useRestApiClient';
import { filterListBy } from 'src/utils/helpers/filter';

const initialState = {
  myMobileApps: null,
  filteredMyMobileApps: [],
  isLoading: false,
  isPreloaded: false,
  filterValues: { industry: '', search_title: '' },
  setFilterValues: () => Promise.resolve(),
  saveMobileApp: () => Promise.resolve(),
  deleteMobileApp: () => Promise.resolve(),
  updateMobileApp: () => Promise.resolve()
};

const MyMobileAppsContext = createContext(initialState);

export const MyMobileAppsProvider = (props) => {
  const { children, ...contextProviderProps } = props;
  const [filterValues, setFilterValues] = useState(initialState.filterValues);
  const [activity, setActivity] = useState(null);
  const [isPreloaded, setPreloaded] = useState(false);
  const [myMobileApps, setMyMobileApps] = useState(initialState.myMobileApps);
  const [filteredMyMobileApps, setFilteredMyMobileApps] = useState(
    initialState.filteredMyMobileApps
  );
  const client = useRestApiClient();

  const deleteMobileApp = async (mobileAppsId) => {
    setActivity('deleting');
    await client.delete(`/apps/${mobileAppsId}`);
    setMyMobileApps((prev) => {
      const filtered = prev.filter((row) => row.id !== mobileAppsId);
      return [...filtered];
    });
    setActivity(null);
  };

  const saveMobileApp = async (payload) => {
    try {
      setActivity('saving');
      const {
        data: { message: newMobileApp }
      } = await client.post('/apps', payload);

      setMyMobileApps((currentList) => {
        return currentList ? [...currentList, newMobileApp] : [newMobileApp];
      });
      setPreloaded(true);
      setActivity(null);
    } catch (error) {
      setActivity(null);
      throw error;
    }
  };

  const updateMobileApp = useCallback(
    async (mobileAppsId, payload, isLocal = false) => {
      // update local copy of record
      if (isLocal) {
        setMyMobileApps((currentList) => {
          const newList = currentList.map((mobileApp) => {
            return mobileApp.id === mobileAppsId
              ? { ...mobileApp, ...payload }
              : mobileApp;
          });
          return [...newList];
        });
      } else {
        let result = {};
        try {
          setActivity('updating');
          const {
            data: { message }
          } = await client.patch(`/apps/${mobileAppsId}`, payload);

          if (message.pending_approval) {
            delete payload.private;
          }

          result = message;
        } catch (error) {
          setActivity(null);
          throw error;
        }
        setActivity(null);

        return result;
      }

      return null;
    },
    [myMobileApps]
  );

  const loadMyMobileApps = async (force = false) => {
    if (myMobileApps?.length > 0 && !force) return;
    if (activity === 'fetching') return;

    try {
      setActivity('fetching');
      const {
        data: { message }
      } = await client.get('/collections/apps');
      setMyMobileApps(message);
      // setFilteredMyMobileApps(message);
      setPreloaded(true);
    } catch (error) {
      setMyMobileApps([]);
      if (error?.response?.status === 404) {
        setPreloaded(true);
      }
      throw error;
    } finally {
      setActivity(null);
    }
  };

  useEffect(() => {
    let filterResult = myMobileApps || [];
    const { industry, search_title: searchTitle } = filterValues;
    filterResult = filterListBy(filterResult, 'industry', industry);

    if (searchTitle) {
      gtagEvent('search', {
        search_term: searchTitle
      });

      filterResult = filterResult.filter(
        ({ description, title }) =>
          (description &&
            description.toLowerCase().indexOf(searchTitle.toLowerCase()) >
              -1) ||
          (title && title.toLowerCase().indexOf(searchTitle.toLowerCase()) > -1)
      );
    }

    setFilteredMyMobileApps([...filterResult]);
  }, [myMobileApps, filterValues]);

  const values = useMemo(
    () => ({
      isPreloaded,
      activity,
      filterValues,
      myMobileApps,
      filteredMyMobileApps,
      setFilterValues,
      loadMyMobileApps,
      saveMobileApp,
      updateMobileApp,
      deleteMobileApp
    }),
    [
      isPreloaded,
      activity,
      filterValues,
      myMobileApps,
      filteredMyMobileApps,
      updateMobileApp
    ]
  );

  return (
    <MyMobileAppsContext.Provider value={values} {...contextProviderProps}>
      {children}
    </MyMobileAppsContext.Provider>
  );
};

MyMobileAppsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default MyMobileAppsContext;
